import dayjs from "configs/dayjs";
import { REGEX_PHONE_NUMBER } from "constants/common/common";
import * as yup from "yup";

export interface POItemFormValues {
  rowKey: string;
  productId: string;
  productName: string;
  productSpec: string;
  productUnit: string;
  quantity: number;
  unitPrice: number;
}

export interface POPartnerFormValues {
  id: string;
  name: string;
  phone: string;
  taxCode: string;
  contactPersonId: string;
  regionId: string;
  notes: string;
}

export interface POCreateFormValues {
  code: string;
  createdAt: number;
  warehouseId: string;
  notes: string;
  partner: POPartnerFormValues;
  items: POItemFormValues[];
}

export const generateRowKey = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const emptyItem = (): POItemFormValues => ({
  rowKey: generateRowKey(),
  productId: "",
  productName: "",
  productSpec: "",
  productUnit: "",
  quantity: 1,
  unitPrice: 0,
});

export const emptyPartner = (): POPartnerFormValues => ({
  id: "",
  name: "",
  phone: "",
  taxCode: "",
  contactPersonId: "",
  regionId: "",
  notes: "",
});

export const initialValues: POCreateFormValues = {
  code: "",
  createdAt: dayjs().unix(),
  warehouseId: "",
  notes: "",
  partner: emptyPartner(),
  items: [emptyItem()],
};

export const createValidationSchema = yup.object().shape({
  warehouseId: yup.string().required("Vui lòng chọn kho"),
  createdAt: yup
    .number()
    .typeError("Vui lòng chọn ngày tạo")
    .min(0)
    .required("Vui lòng chọn ngày tạo"),
  notes: yup.string().optional(),
  partner: yup.object().shape({
    id: yup.string(),
    name: yup
      .string()
      .min(2, "Tên phải có ít nhất 2 ký tự")
      .required("Vui lòng nhập tên nhà cung cấp"),
    phone: yup
      .string()
      .matches(REGEX_PHONE_NUMBER, { message: "Số điện thoại không hợp lệ", excludeEmptyString: true })
      .optional(),
    // Khi chọn đối tác có sẵn (partner.id có giá trị) thì form khoá các ô này lại
    // (disabled={isExisting}) và backend cũng bỏ qua chúng — resolvePartner nạp
    // đối tác theo id rồi lờ mọi field còn lại. Bắt buộc chúng lúc đó sẽ tạo ra
    // ô vừa trống, vừa bị khoá, vừa chặn submit: nút bấm không phản ứng gì mà
    // người dùng không sửa được. Chỉ bắt buộc khi đang tạo đối tác mới.
    taxCode: yup
      .string()
      .when("id", {
        is: (id?: string) => !id,
        then: (schema) => schema.required("Vui lòng nhập mã số thuế"),
        otherwise: (schema) => schema.optional(),
      }),
    contactPersonId: yup.string().optional(),
    regionId: yup
      .string()
      .when("id", {
        is: (id?: string) => !id,
        then: (schema) => schema.required("Vui lòng chọn địa bàn"),
        otherwise: (schema) => schema.optional(),
      }),
    notes: yup.string().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        productId: yup.string().required("Vui lòng chọn sản phẩm"),
        productName: yup.string().required(),
        productUnit: yup.string().required(),
        quantity: yup
          .number()
          .typeError("Vui lòng nhập số lượng")
          .positive("Số lượng phải lớn hơn 0")
          .required("Vui lòng nhập số lượng"),
        unitPrice: yup
          .number()
          .typeError("Vui lòng nhập đơn giá")
          .min(0, "Đơn giá không được âm")
          .required("Vui lòng nhập đơn giá"),
      }),
    )
    .min(1, "Cần ít nhất 1 sản phẩm"),
});
